import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdFaIconIndexEntry, HdFaIconIndexService } from './hd-fa-icon-index.service';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/** Handvol logische groep-iconen voor de één-klik-rij; de rest is vindbaar via zoeken. */
export const HD_ICON_PICKER_SUGGESTIONS: readonly string[] = [
  'folder', 'folder-open', 'folder-tree', 'users', 'user-group', 'people-roof', 'people-group',
  'user', 'graduation-cap', 'chalkboard-user', 'book', 'book-open', 'briefcase', 'heart',
  'star', 'tag', 'calendar-days', 'globe', 'house', 'city', 'sun', 'moon', 'bolt', 'eye',
  'rocket', 'flask', 'music', 'palette', 'seedling', 'mountain',
];

const MAX_RESULTS = 60;

/**
 * Icoon-kiezer voor groepen: suggestierij (één klik) + zoekveld over de volledige
 * FA Pro-namenlijst (lazy geladen index; de webfont is toch al aanwezig, dus elk
 * resultaat rendert direct als echt icoon).
 */
@Component({
  selector: 'hd-icon-picker',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './hd-icon-picker.component.html',
  styleUrl: './hd-icon-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdIconPickerComponent {
  private readonly indexService = inject(HdFaIconIndexService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Gekozen bare FA-naam; null = geen icoon (standaard). */
  readonly value = model<string | null>(null);

  protected readonly searchTerm = signal('');
  private readonly index = signal<readonly HdFaIconIndexEntry[] | null>(null);
  protected readonly indexError = signal(false);

  protected readonly suggestions = HD_ICON_PICKER_SUGGESTIONS;

  constructor() {
    // Dialog-inhoud rendert pas bij openen → dit ís al lazy t.o.v. de app.
    void this.indexService.load()
      .then(entries => this.index.set(entries))
      .catch(() => this.indexError.set(true));
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      searchPlaceholder: translate('humanDesign.chartManager.iconPicker.searchPlaceholder'),
      noIcon: translate('humanDesign.chartManager.iconPicker.noIcon'),
      noResults: translate('humanDesign.chartManager.iconPicker.noResults'),
      loading: translate('humanDesign.chartManager.detail.loading'),
    };
  });

  /** Zoekresultaten (max 60) over naam + officiële zoektermen; null = geen zoekterm actief. */
  protected readonly results = computed<{ icons: string[]; total: number } | null>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return null;
    const index = this.index();
    if (!index) return { icons: [], total: -1 }; // -1 = index laadt nog

    const matches: string[] = [];
    let total = 0;
    for (const [name, terms] of index) {
      if (!name.includes(term) && !terms.includes(term)) continue;
      total++;
      if (matches.length < MAX_RESULTS) matches.push(name);
    }
    return { icons: matches, total };
  });

  protected select(icon: string): void {
    this.value.set(this.value() === icon ? null : icon);
  }

  protected clear(): void {
    this.value.set(null);
  }
}
